import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Cake } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useCalendars, useTimelines } from "@/lib/queries";

const MODES = [["today", "Today"], ["date", "On a date"], ["timeline", "At a timeline moment"]];

function AsOf({ mode, q, setQ, calendars, timelines }) {
  const cal = calendars.find((c) => c.id === q.calendar_id) || calendars[0];
  const linked = timelines.filter((t) => calendars.some((c) => c.link?.timeline_id === t.id));
  if (mode === "date" && cal) return (
    <div className="flex flex-wrap gap-2" data-testid="age-date-fields">
      <Select value={cal.id} onValueChange={(v) => setQ({ ...q, calendar_id: v, month: 0 })}>
        <SelectTrigger className="h-9 w-48" data-testid="age-calendar-select"><SelectValue /></SelectTrigger>
        <SelectContent>{calendars.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
      </Select>
      <Input type="number" min={1} className="h-9 w-16 font-mono-lf" value={q.day} onChange={(e) => setQ({ ...q, day: e.target.value })} data-testid="age-day-input" />
      <Select value={String(q.month)} onValueChange={(v) => setQ({ ...q, month: parseInt(v, 10) })}>
        <SelectTrigger className="h-9 w-36" data-testid="age-month-select"><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-72">{cal.month_names.map((n, i) => <SelectItem key={i} value={String(i)}>{n}</SelectItem>)}</SelectContent>
      </Select>
      <Input type="number" className="h-9 w-24 font-mono-lf" value={q.year} onChange={(e) => setQ({ ...q, year: e.target.value })} placeholder="Year" data-testid="age-year-input" />
    </div>
  );
  if (mode === "timeline") return linked.length === 0 ? <p className="text-xs text-[#A8A090]">Link a calendar to a timeline to measure age at a timeline moment.</p> : (
    <div className="flex flex-wrap gap-2" data-testid="age-timeline-fields">
      <Select value={q.timeline_id || linked[0].id} onValueChange={(v) => setQ({ ...q, timeline_id: v })}>
        <SelectTrigger className="h-9 w-48" data-testid="age-timeline-select"><SelectValue /></SelectTrigger>
        <SelectContent>{linked.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
      </Select>
      <Input type="number" className="h-9 w-28 font-mono-lf" value={q.t} onChange={(e) => setQ({ ...q, t: e.target.value })} placeholder="T" data-testid="age-t-input" />
    </div>
  );
  return null;
}

function params(mode, q, calendars, timelines) {
  const p = new URLSearchParams();
  if (q.birthday_id) p.set("birthday_id", q.birthday_id);
  if (mode === "date" && q.year !== "") {
    p.set("calendar_id", q.calendar_id || calendars[0]?.id); p.set("year", q.year); p.set("month", q.month); p.set("day", q.day || 1);
  }
  if (mode === "timeline" && q.t !== "") {
    const tid = q.timeline_id || timelines.find((t) => calendars.some((c) => c.link?.timeline_id === t.id))?.id;
    if (tid) { p.set("timeline_id", tid); p.set("t", q.t); }
  }
  return p.toString();
}

function Results({ data }) {
  if (!data.born) return <p className="text-sm text-[#F59E0B]" data-testid="age-not-born">Not yet born at this moment — {Math.abs(data.earth_years)} Earth years to go.</p>;
  return (
    <>
      <p className="font-display text-3xl text-[#F3EBDD]" data-testid="age-headline">{Math.floor(data.earth_years)} <span className="text-lg text-[#A8A090]">Earth years · {data.earth_days.toLocaleString()} Earth days lived</span></p>
      <div className="mt-3 divide-y divide-[#2D3442] rounded-md border border-[#2D3442]" data-testid="age-results">
        {data.results.map((r) => (
          <div key={r.calendar_id} className="grid grid-cols-[1fr_auto] gap-x-4 px-3 py-2 text-sm" data-testid={`age-row-${r.calendar_id}`}>
            <span className="flex items-center gap-2 text-[#E8DFD1]"><span className="h-2 w-2 rounded-full" style={{ background: r.color }} />{r.name}{r.is_birth_calendar && <Cake className="h-3.5 w-3.5 text-[#F59E0B]" />}</span>
            <span className="text-right text-[#F3EBDD]">{r.out_of_range ? "out of range" : <>{r.years} years <span className="text-[#A8A090]">· {r.days.toLocaleString()} days</span></>}</span>
            {!r.out_of_range && <span className="col-span-2 text-xs text-[#A8A090]">Born {r.birth_label} · as of {r.as_of_label}</span>}
          </div>
        ))}
      </div>
    </>
  );
}

export const AgeCard = ({ ent }) => {
  const uid = ent.universe_id;
  const { data: calendars = [] } = useCalendars(uid);
  const { data: timelines = [] } = useTimelines(uid);
  const [mode, setMode] = useState("today");
  const [q, setQ] = useState({ birthday_id: "", calendar_id: "", year: "", month: 0, day: 1, timeline_id: "", t: "" });
  const qs = params(mode, q, calendars, timelines);
  const { data, error } = useQuery({ queryKey: ["cal", "age", ent.id, qs], queryFn: () => api.get(`/entities/${ent.id}/age?${qs}`).then((r) => r.data), retry: false });
  if (!data?.birthdays?.length) return null;
  const usable = data.birthdays.filter((b) => b.usable);
  return (
    <section className="mt-8 rounded-md border border-[#F59E0B]/25 bg-[#F59E0B]/[0.03] p-4" data-testid="age-card">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="lf-label flex items-center gap-1.5"><Cake className="h-3.5 w-3.5 text-[#F59E0B]" />Age</div>
        {usable.length > 1 && (
          <Select value={data.birthday_id} onValueChange={(v) => setQ({ ...q, birthday_id: v })}>
            <SelectTrigger className="h-8 w-56 text-xs" data-testid="age-birthday-select"><SelectValue /></SelectTrigger>
            <SelectContent>{usable.map((b) => <SelectItem key={b.event_id} value={b.event_id}>{b.title} · {b.calendar}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      {!usable.length ? <p className="text-sm text-[#A8A090]" data-testid="age-no-year">The birthday event has no birth year. Edit it and set “First year” to calculate age.</p> : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {MODES.map(([m, l]) => (
              <button key={m} type="button" onClick={() => setMode(m)} data-testid={`age-mode-${m}`}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${mode === m ? "border-[#F59E0B] text-[#F59E0B]" : "border-[#2D3442] text-[#A8A090] hover:text-[#F3EBDD]"}`}>{l}</button>
            ))}
          </div>
          <div className="mb-3"><AsOf mode={mode} q={q} setQ={setQ} calendars={calendars} timelines={timelines} /></div>
          {error ? <p className="text-sm text-red-400" data-testid="age-error">{errMsg(error)}</p> : data.results.length > 0 && <Results data={data} />}
        </>
      )}
    </section>
  );
};
